import React, { useEffect, useMemo, useRef, useState } from "react";

import { type BodyPart } from "../../../generator/_Medical/BodyPart/BodyPart.model";
import { Svg, SvgContainer, SvgPointer } from "./InteractiveSvg.styled";

export interface IPosition {
  absoluteX: number;
  absoluteY: number;
}

interface Props {
  svgFile: string;
  selectedParts: BodyPart[]; // Array of selected parts
  setSelectedParts?: (data: BodyPart[]) => void;
  type?: "front" | "back";
  preview?: boolean;
}

const InteractiveSvg = ({
  svgFile,
  selectedParts,
  setSelectedParts,
  type,
  preview,
}: Props) => {
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [defaultFill, setDefaultFill] = useState({
    color: "",
    opacity: "",
  });

  const handleClick = (event: React.MouseEvent<SVGElement>) => {
    if (preview || !setSelectedParts) return;
    const target = event.target as SVGElement;
    const label = target.getAttribute("data-label"); // Replace with "id" if you're using IDs
    // Get the bounding box of the SVG container
    const svgContainer = svgContainerRef.current?.getBoundingClientRect();
    if (svgContainer && label) {
      // Calculate click position relative to the SVG
      const clickX = event.clientX - svgContainer.left;
      const clickY = event.clientY - svgContainer.top;

      // Get the bounding box of the clicked path

      // Remove any existing points that are close to the current clicked position
      const filteredParts = selectedParts.filter(item => {
        if (!item.position || item.type !== type) return true;
        const distance = Math.sqrt(
          Math.pow(item.position.absoluteX - clickX, 2) +
            Math.pow(item.position.absoluteY - clickY, 2),
        );
        return distance > 16; // 16 is the diameter of the marker
      });
      if (filteredParts.length === selectedParts.length) {
        setSelectedParts([
          ...filteredParts,
          {
            selectedPart: label,
            position: {
              absoluteX: clickX,
              absoluteY: clickY,
            },
            type: type ?? null,
          },
        ]);
      } else {
        setSelectedParts(filteredParts);
      }
    }
  };
  const filteredData = useMemo(
    () => selectedParts.filter(item => item.type === type),
    [selectedParts, type],
  );

  useEffect(() => {
    if (svgRef.current) {
      const paths = svgRef.current.querySelectorAll("path");
      const defaultPath = paths[0];
      setDefaultFill({
        color: defaultPath.style.fill,
        opacity: defaultPath.style.fillOpacity,
      });
    }
  }, []);

  useEffect(() => {
    if (svgRef.current) {
      const paths = svgRef.current.querySelectorAll("path");
      paths.forEach(path => {
        const label = path.getAttribute("data-label");
        if (label) {
          const isSelected = filteredData.some(
            item => item.selectedPart === label,
          );
          if (isSelected) {
            path.style.fill = "#036ffc";
            path.style.fillOpacity = "0.4";
          } else {
            path.style.fill = defaultFill.color;
            path.style.fillOpacity = defaultFill.opacity;
          }
        }
      });
    }
  }, [filteredData, defaultFill]);

  return (
    <SvgContainer ref={svgContainerRef}>
      <Svg
        ref={svgRef}
        style={!preview ? { cursor: "pointer" } : {}}
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: svgFile }}
      />

      {filteredData.map(
        (item, index) =>
          item.position && (
            <SvgPointer
              key={index}
              style={{
                left: item.position.absoluteX - 8, // Adjust for marker size
                top: item.position.absoluteY - 8, // Adjust for marker size
              }}
            />
          ),
      )}
    </SvgContainer>
  );
};

export default InteractiveSvg;
